import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Link, Outlet, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { UserButton } from '@clerk/clerk-react'

import { EricOnlineDot } from '@/components/dashboard/agentic/EricOnlineDot'
import { OfflineBanner } from '@/components/OfflineBanner'
import { ProductionTenantBanner } from '@/components/ProductionTenantBanner'
import { WhatsNewDialog } from '@/components/WhatsNewDialog'
import { track } from '@/lib/analytics'
import { pageNameFor } from '@/lib/page-name'

import { CommandDock } from '@/components/dock/CommandDock'

import { ShellContext } from './shell-context'
import { SideRail } from './SideRail'
import { TabBar } from './TabBar'

/**
 * Tenant app shell (DESIGN.md §4.4, §5): top strip (logo + agent dot +
 * user menu), bottom TabBar on phone, SideRail at ≥1024px. Routes are
 * unchanged — this is navigation presentation only. The admin host keeps
 * AuthedLayout (renamed in the cleanup PR).
 */
export function AppShell() {
  const { t } = useTranslation('shell')
  const location = useLocation()
  const lastTrackedPageRef = useRef<string | null>(null)
  const [whatsNewOpen, setWhatsNewOpen] = useState(false)

  // page.viewed fires on actual navigation only, not on re-renders —
  // identical contract to the previous AuthedLayout tracker (§9: existing
  // event names are invariant).
  useEffect(() => {
    const page = pageNameFor(location.pathname, false)
    if (lastTrackedPageRef.current === page) return
    lastTrackedPageRef.current = page
    track('page.viewed', { page })
  }, [location.pathname])

  const [dockSuppressions, setDockSuppressions] = useState(0)
  const shell = useMemo(
    () => ({
      openWhatsNew: () => setWhatsNewOpen(true),
      suppressDock: () => {
        setDockSuppressions((n) => n + 1)
        let released = false
        return () => {
          if (released) return
          released = true
          setDockSuppressions((n) => n - 1)
        }
      },
    }),
    [],
  )

  // --shell-bottom-inset is MEASURED now (PR-4): the bottom stack's
  // height varies with dock content (staged work, receipts) and
  // breakpoint (TabBar hides at lg while the dock stays). The static
  // class below is only the pre-measure approximation for first paint.
  const rootRef = useRef<HTMLDivElement>(null)
  const stackRef = useRef<HTMLDivElement>(null)
  useLayoutEffect(() => {
    const root = rootRef.current
    const stack = stackRef.current
    if (!root || !stack) return
    const apply = () =>
      root.style.setProperty('--shell-bottom-inset', `${stack.offsetHeight}px`)
    apply()
    const ro = new ResizeObserver(apply)
    ro.observe(stack)
    return () => ro.disconnect()
  }, [])

  return (
    <ShellContext.Provider value={shell}>
      {/* --shell-bottom-inset = the measured height of the fixed bottom
          stack (dock + TabBar). Page-level `sticky bottom-*` rows and
          main's padding offset by it so nothing hides under the stack.
          The class value is the first-paint approximation only — the
          ResizeObserver above overwrites it inline. */}
      <div
        ref={rootRef}
        className="min-h-dvh bg-background [--shell-bottom-inset:calc(61px+max(2px,env(safe-area-inset-bottom)))] lg:grid lg:grid-cols-[236px_minmax(0,1fr)]"
      >
        <SideRail />

        <div className="flex min-h-dvh min-w-0 flex-col">
          <header className="sticky top-0 z-30 border-b border-border bg-background">
            <div className="mx-auto flex w-full max-w-7xl items-center gap-3 px-4 py-3 sm:px-6">
              <Link to="/" className="flex shrink-0 items-center gap-2.5">
                <span
                  aria-hidden="true"
                  className="grid size-7 shrink-0 place-items-center rounded-[9px] bg-saved text-[15px] font-extrabold text-white"
                >
                  a
                </span>
                {/* "autoflo" is the product brand name — kept literal. */}
                <span className="text-lg font-bold tracking-tight text-foreground">
                  autoflo
                </span>
                <span className="sr-only">{t('header.home_link')}</span>
              </Link>

              <div className="ml-auto flex items-center">
                <EricOnlineDot />
              </div>
              <UserButton />
            </div>
          </header>

          {/* Status strips ride ONE sticky wrapper: the offline strip must
              stay visible when it appears mid-scroll (its whole job), and
              two same-offset sticky siblings would overlap once both show
              (Mira's EMSERVICE session + offline). PTB keeps its own sticky
              classes for its AdminLayout mount — nested same-top sticky
              inside the stuck wrapper is a visual no-op. */}
          <div className="sticky top-[57px] z-20">
            <ProductionTenantBanner />
            <OfflineBanner />
          </div>

          {/* Bottom padding clears the fixed bottom stack (dock + TabBar)
              at every breakpoint — the dock exists on desktop too. */}
          <main className="mx-auto w-full max-w-7xl flex-1 px-4 pt-6 pb-[calc(var(--shell-bottom-inset)+16px)] sm:px-6">
            <Outlet />
          </main>

        </div>

        {/* The fixed bottom stack: composer dock above, tab bar below
            (§4.3). One stack so the inset measurement covers both. */}
        <div
          ref={stackRef}
          className="fixed inset-x-0 bottom-0 z-30 lg:left-[236px]"
        >
          <CommandDock suppressed={dockSuppressions > 0} />
          <TabBar />
        </div>

        {/* Mounted at shell level so the auto-pop on unseen releases works
            from any page; More's row re-surfaces it via useShell(). */}
        <WhatsNewDialog
          forceOpen={whatsNewOpen}
          onOpenChange={(next) => setWhatsNewOpen(next)}
        />
      </div>
    </ShellContext.Provider>
  )
}
